// src/components/Header.js
import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/Header.css';
import { useWallet } from './WalletContext';
import WalletModal from '../components/WalletModal';
import { ThemeContext } from '../components/ThemeContext';

const Header = () => {
  const { walletAddress, isConnected, connectWallet } = useWallet();
  const { isDarkMode } = useContext(ThemeContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const shortenAddress = (address) => 
    address ? `${address.substring(0, 6)}...${address.substring(address.length - 4)}` : '';

  const onWalletClick = () => {
    if (isConnected) {
      setIsModalOpen(true);
    } else {
      connectWallet();
    }
  };

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navItems = [
    { path: '/drops', icon: 'fa-rocket', label: 'Drops', emoji: '🚀' },
    { path: '/collections', icon: 'fa-th', label: 'Collections', emoji: '🎨' },
    { path: '/mint', icon: 'fa-image', label: 'Mint', emoji: '✨' },
    { path: '/stats', icon: 'fa-compass', label: 'Explore', emoji: '🔍' },
    { path: '/creator', icon: 'fa-paint-brush', label: 'Creator', emoji: '🖌️' },
    { path: '/rewards', icon: 'fa-trophy', label: 'Rewards', emoji: '🏆' },
  ];

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          {/* Logo Section */}
          <div className="header-left">
            <Link to="/" className="brand-link">
              <div className="logo-wrapper">
                <img src="/logoicon2.png" alt="Rare Canvas Logo" className="logo-icon" />
                <div className="logo-glow"></div>
              </div>
              <span className="brand-name">
                <span className="rare">Rare</span>
                <span className="canvas">Canvas</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="header-nav desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                title={item.label}
              >
                <i className={`fas ${item.icon}`}></i>
                <span className="nav-label">{item.label}</span>
                {isActiveRoute(item.path) && <span className="active-indicator"></span>}
              </Link>
            ))}

            {/* Wallet Button */}
            <button
              className={`nav-link wallet-button ${isConnected ? 'connected' : ''}`}
              onClick={onWalletClick}
              title={isConnected ? shortenAddress(walletAddress) : "Connect Wallet"}
            >
              <i className="fas fa-wallet"></i>
              <span className="nav-label">
                {isConnected ? shortenAddress(walletAddress) : 'Connect'}
              </span>
              {isConnected && <span className="connected-dot"></span>}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className={`mobile-menu-button ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-content">
          {/* Mobile Wallet Status */}
          <div className="mobile-wallet-section">
            <button
              className={`mobile-wallet-button ${isConnected ? 'connected' : ''}`}
              onClick={onWalletClick}
            >
              <div className="mobile-wallet-icon">
                <i className="fas fa-wallet"></i>
                {isConnected && <span className="wallet-status-dot"></span>}
              </div>
              <div className="mobile-wallet-info">
                <span className="wallet-label">
                  {isConnected ? 'Connected' : 'Not Connected'}
                </span>
                <span className="wallet-address">
                  {isConnected ? shortenAddress(walletAddress) : 'Connect your wallet'}
                </span>
              </div>
              <i className="fas fa-chevron-right wallet-arrow"></i>
            </button>
          </div>

          {/* Mobile Navigation Links */}
          <nav className="mobile-nav-links">
            {navItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-nav-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="mobile-nav-icon">
                  <i className={`fas ${item.icon}`}></i>
                </div>
                <span className="mobile-nav-label">{item.label}</span>
                {isActiveRoute(item.path) && <span className="mobile-active-dot"></span>}
                <i className="fas fa-chevron-right nav-arrow"></i>
              </Link>
            ))}
          </nav>

          {/* Mobile Footer Info */}
          <div className="mobile-nav-footer">
            <p className="mobile-version">Version 2.0.3</p>
            <p className="mobile-copyright">© 2023-2026 Rare Canvas</p>
          </div>
        </div>
      </div>

      {/* Wallet Modal */}
      <WalletModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        walletAddress={walletAddress} 
      />
    </>
  );
};

export default Header;